/**
 * Generates an array of pagination items (page numbers or ellipses).
 *
 * @param currentPage The current active page number (1-indexed).
 * @param totalPages The total number of available pages.
 * @param maxVisibleItems The desired total number of items (numbers or '...') to display
 * @returns An array of numbers or strings ('...') representing the pagination.
 */
export const getPaginationLabels = (
  currentPage: number,
  totalPages: number,
  maxVisibleItems: number = 6,
): (number | string)[] => {
  // Case 1: Total pages are less than or equal to the maximum visible items. Show all pages.
  if (totalPages <= maxVisibleItems) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const finalPages: Array<number | string> = [];
  const edgeBoundary = Math.floor(maxVisibleItems / 2); // Used to determine "near start" or "near end" zones

  // Case A: Current page is near the beginning
  // If currentPage is within the first `edgeBoundary` pages (e.g., 1, 2, 3 for 6 items)
  // We display 1, 2, ..., (maxVisibleItems - 1), '...', totalPages
  if (currentPage <= edgeBoundary) {
    for (let i = 1; i <= maxVisibleItems - 2; i++) {
      finalPages.push(i);
    }
    finalPages.push("...");
    finalPages.push(totalPages);
  }
  // Case B: Current page is near the end
  // If currentPage is within the last `edgeBoundary` pages (e.g., totalPages-2, totalPages-1, totalPages for 6 items)
  // We display 1, '...', totalPages - (maxVisibleItems - 2), ..., totalPages
  else if (currentPage >= totalPages - edgeBoundary + 1) {
    finalPages.push(1);
    finalPages.push("...");
    for (let i = totalPages - (maxVisibleItems - 3); i <= totalPages; i++) {
      finalPages.push(i);
    }
  }
  // Case C: Current page is in the middle (requires two ellipses)
  // We display 1, '...', [two middle pages], '...', totalPages
  else {
    finalPages.push(1);
    finalPages.push("...");

    // To maintain exactly 6 items (2 fixed ends + 2 ellipses = 4), we have 2 slots for middle pages.
    // We prioritize the currentPage and one of its immediate neighbors.
    // If currentPage + 1 is still a valid page (not totalPages itself), we use currentPage and currentPage + 1.
    // Otherwise, if currentPage + 1 is too close to totalPages, we use currentPage - 1 and currentPage.
    // (Note: The `else if` for Case B already handles scenarios where currentPage + 1 is near totalPages,
    // so this `if` here will typically be true, meaning currentPage + 1 is not totalPages yet.)
    if (currentPage + 1 < totalPages) {
      finalPages.push(currentPage);
      finalPages.push(currentPage + 1);
    } else {
      // This fallback mostly for edge cases where currentPage might be totalPages - 1 (handled by Case B normally)
      // But included for robustness.
      finalPages.push(currentPage - 1);
      finalPages.push(currentPage);
    }

    finalPages.push("...");
    finalPages.push(totalPages);
  }

  return finalPages;
};
